import Link from 'components/Globals/Base/Link';
import { NextCustomImage } from 'components/Image';
import HighlightedStats from 'components/HighlightedStats';
import PresentedBy, { type PresentedByProps } from 'components/PresentedBy';
import RawHtml from 'utils/miscUtils/rawHtml';
import type { FranchiseListItem } from 'interfaces/content/Ranking';
import DataTable from 'components/DataTable';
import NextPreviousNav from 'components/NextPreviousNav';
import type { EventData } from 'services/Gtm/entities';
import { RightRailFlexArticles } from 'components/Ad/AdSlot';
import useBreakpoint from 'hooks/useBreakpoint';
import SimpleInStream from 'components/Ad/AdSlot/SimpleInStream';
import { Microsites } from 'constants/constants';
import getWellRankingListItemTestId from 'constants/testsIds/wellRankingListItem';
import { parseCaptionWhenAltTextEmpty, parseContentForEmptySpace } from 'utils/parsingUtils/clientSideParsing';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import { AppErrorSeverity } from 'utils/log/constants/errorSeverity';
import {
  AboutTitle,
  AboutWrapper,
  ContentWrapper,
  CreditWrapper,
  Description,
  HeadContainer,
  ImageWrapper,
  RightRail,
  TitleRanking,
  TitleWrapper,
  Wrapper,
  WrapperDesc,
} from './PeopleRankingPageStyles';

export interface PeopleRankingPageProps {
  franchiseItem: FranchiseListItem;
  trackingData: EventData;
  presentedBy?: PresentedByProps;
}

const PeopleRankingPage = ({ franchiseItem, trackingData, presentedBy }: PeopleRankingPageProps) => {
  const isDesktop = useBreakpoint('xlMin');
  const isMobile = useBreakpoint('mdMax');
  const isTablet = !isMobile && !isDesktop;

  return (
    <div className='franchise-content'>
      {Microsites.Well === franchiseItem.platformCD && presentedBy && <PresentedBy {...presentedBy} />}

      <ErrorBoundary fragment={UIFragments.WELL_PEOPLE_RANKING_PAGE}>
        <HeadContainer>
          <TitleRanking
            as='h2'
            $fontFamily='graphikCond'
            $size={{ default: 'level2', lgMin: 'xlarge', mdMin: 'large' }}
          >
            <Link
              href={franchiseItem.listSlug}
              trackingData={
                trackingData && {
                  ...trackingData,
                  eventAction: `${franchiseItem.listTitle}`,
                  eventLabel: `title click: ${franchiseItem.title}`,
                }
              }
            >
              {franchiseItem.listTitle}
            </Link>
          </TitleRanking>
        </HeadContainer>
      </ErrorBoundary>

      <ErrorBoundary fragment={UIFragments.WELL_PEOPLE_RANKING_PAGE}>
        <Wrapper>
          <TitleWrapper>
            <NextPreviousNav
              color={franchiseItem.color}
              showRank={!franchiseItem.hideRanking}
              next={franchiseItem.buttons.next}
              previous={franchiseItem.buttons.previous}
              rank={`${franchiseItem.rank}`}
            />
            <AboutWrapper>
              <AboutTitle
                as='h1'
                $fontFamily='graphikCond'
                $size={{ default: 'level2', lgMin: 'xlarge', mdMin: 'large' }}
                data-cy={getWellRankingListItemTestId('TITLE')}
              >
                <span> {franchiseItem.title}</span>
              </AboutTitle>
            </AboutWrapper>
          </TitleWrapper>
          {franchiseItem.image && (
            <ImageWrapper>
              <NextCustomImage
                src={franchiseItem.image.mediaItemUrl}
                alt={parseCaptionWhenAltTextEmpty(franchiseItem.image)}
                width={1024}
                height={720}
                data-cy={getWellRankingListItemTestId('IMAGE')}
              />
              <CreditWrapper>
                {RawHtml({ html: franchiseItem.image.caption })}
                <span>{RawHtml({ html: franchiseItem.image.credit })}</span>
              </CreditWrapper>
            </ImageWrapper>
          )}
        </Wrapper>
      </ErrorBoundary>

      <ErrorBoundary fragment={UIFragments.WELL_PEOPLE_RANKING_PAGE}>
        <HighlightedStats
          stats={franchiseItem.highlights}
          dataCy={getWellRankingListItemTestId('HIGHLIGHTED_STATS')}
        />
      </ErrorBoundary>

      <ErrorBoundary
        fragment={UIFragments.WELL_PEOPLE_RANKING_PAGE}
        severity={AppErrorSeverity.HIGH}
      >
        <WrapperDesc>
          <ContentWrapper>
            <Description data-cy={getWellRankingListItemTestId('DESCRIPTION')}>
              {RawHtml({
                html: parseContentForEmptySpace(franchiseItem.description),
              })}
            </Description>
            {franchiseItem.tables.map((table) => (
              <DataTable
                key={table.title}
                platformCD={franchiseItem.platformCD}
                {...table}
                dataCy={getWellRankingListItemTestId('TABLE_ROW')}
              />
            ))}
            {isTablet && <SimpleInStream id={0} />}
          </ContentWrapper>
          {isDesktop && (
            <RightRail>
              <RightRailFlexArticles
                id={0}
                dataCy={getWellRankingListItemTestId('AD')}
              />
            </RightRail>
          )}
        </WrapperDesc>
      </ErrorBoundary>
    </div>
  );
};

export default PeopleRankingPage;
